import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/kyc';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { useNotification } from 'hooks/ui/useNotification';
import { api } from 'lib/httpClient';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';

const useInvestorKyc = (investorId?: string) =>
  useQuery({
    queryKey: ['investor-kyc', investorId],
    queryFn: async () => {
      const {
        data: { data },
      } = await api.get<ApiResponse<Api.GetKycDetailsResponse>>(
        `kyc/${investorId}`,
      );
      return data;
    },
    placeholderData: keepPreviousData,
    enabled: !!investorId,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME_MS,
  });

const useInvestorExpiredKyc = (investorId?: string) =>
  useQuery({
    queryKey: ['investor-expired-kyc', investorId],
    queryFn: async () => {
      const {
        data: { data },
      } = await api.get<ApiResponse<Api.GetKycExpiredResultsResponse>>(
        `kyc/expired/${investorId}`,
      );
      return data;
    },
    placeholderData: keepPreviousData,
    enabled: !!investorId,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME_MS,
  });

const useKycMutation = <ResponseType, RequestType = void>({
  endpoint,
  investorId,
  suppressNotification = false,
  optimisticUpdate,
}: {
  endpoint: string;
  investorId?: string;
  suppressNotification?: boolean;
  optimisticUpdate?: (oldData: any, variables: RequestType) => any;
}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    throwOnError: false,
    mutationFn: async (data: RequestType) => {
      const result = await api.post<ApiResponse<ResponseType>>(
        `kyc/${endpoint}/${investorId}`,
        data,
      );
      return result.data;
    },
    onMutate: async (variables) => {
      if (!optimisticUpdate) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['investor-kyc', investorId],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([
        'investor-kyc',
        investorId,
      ]);

      // Optimistically update to the new value
      const updatedData = optimisticUpdate(previousData, variables);
      queryClient.setQueryData(['investor-kyc', investorId], updatedData);

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, _, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(
          ['investor-kyc', investorId],
          context.previousData,
        );
      }
      handleApiException(err);
    },
    onSuccess: async (data) => {
      if (suppressNotification) return;

      notification.success(data.responseMsg ?? '');
      // Only invalidate if no optimistic update was used
      if (!optimisticUpdate) {
        await queryClient.invalidateQueries({
          queryKey: ['investor-kyc', investorId],
        });
      }
    },
  });
};

const useGetKycImages = (investorId?: string) => {
  const { refetch } = useQuery({
    queryKey: ['investor-kyc-images', investorId],
    queryFn: async () => {
      const {
        data: { data },
      } = await api.get<ApiResponse<Api.GetKycImagesResponse>>(
        `kyc/images/${investorId}`,
      );
      return data;
    },
    placeholderData: keepPreviousData,
    enabled: false,
    staleTime: QUERY_STALE_TIME_MS,
  });

  return () => refetch();
};

const useAmlResultInformation = (investorId?: string, caDocId?: string) => {
  return useQuery({
    enabled: !!investorId && !!caDocId,
    queryKey: ['aml-result-information', investorId, caDocId],
    queryFn: async () => {
      const {
        data: { data },
      } = await api.get<ApiResponse<any>>(
        `kyc/aml-result-information/${investorId}/${caDocId}`,
      );
      return data;
    },
  });
};

// Shared optimistic update function for archive/unarchive operations
const createAmlHitArchiveOptimisticUpdate = (archived: boolean) => {
  return (
    oldData: Api.GetKycDetailsResponse,
    variables: Api.ArchiveAmlHitRequest | Api.UnarchiveAmlHitRequest,
  ): Api.GetKycDetailsResponse => {
    if (!oldData?.amlPotentialMatches) return oldData;

    return {
      ...oldData,
      amlPotentialMatches: oldData.amlPotentialMatches.map((match) =>
        match.caDocId === variables.caDocId ? { ...match, archived } : match,
      ),
    };
  };
};

export default function useKyc(investorId?: string, caDocId?: string) {
  const kycDetails = useInvestorKyc(investorId);
  const expiredKyc = useInvestorExpiredKyc(investorId);
  const getKycImages = useGetKycImages(investorId);
  const amlResultInformation = useAmlResultInformation(investorId, caDocId);

  const { mutate: resetKyc } = useKycMutation<Api.ResetKycResponse>({
    endpoint: 'reset',
    investorId,
  });

  const { mutate: approveIdv } = useKycMutation<
    Api.ApproveIdvResponse,
    Api.ApproveIdvRequest
  >({
    endpoint: 'approve-idv',
    investorId,
  });

  const { mutate: approveAml } = useKycMutation<
    Api.ApproveAmlResponse,
    Api.ApproveAmlRequest
  >({
    endpoint: 'approve-aml',
    investorId,
  });

  const { mutate: unapproveAml } = useKycMutation<
    Api.UnapproveAmlResponse,
    Api.UnapproveAmlRequest
  >({
    endpoint: 'unapprove-aml',
    investorId,
  });

  const { mutate: manualKycProcess } = useKycMutation<
    Api.ManualKycProcessRequest,
    Api.ManualKycProcessRequest
  >({
    endpoint: 'manual-kyc-process',
    investorId,
  });

  const { mutate: saveAmlApprovalNotes } = useKycMutation<
    Api.SaveAmlApprovalNotesResponse,
    Api.SaveAmlApprovalNotesRequest
  >({
    endpoint: 'aml-approval-notes',
    investorId,
  });

  const { mutate: saveIdvApprovalNotes } = useKycMutation<
    Api.SaveIdvApprovalNotesResponse,
    Api.SaveIdvApprovalNotesRequest
  >({
    endpoint: 'idv-approval-notes',
    investorId,
  });

  const { mutate: archiveAmlHit } = useKycMutation<
    Api.ArchiveAmlHitResponse,
    Api.ArchiveAmlHitRequest
  >({
    endpoint: 'archive-aml-hit',
    investorId,
    optimisticUpdate: createAmlHitArchiveOptimisticUpdate(true),
  });

  const { mutate: unarchiveAmlHit } = useKycMutation<
    Api.UnarchiveAmlHitResponse,
    Api.UnarchiveAmlHitRequest
  >({
    endpoint: 'unarchive-aml-hit',
    investorId,
    optimisticUpdate: createAmlHitArchiveOptimisticUpdate(false),
  });

  const { mutate: markAmlResultsAsViewed } = useKycMutation<
    Api.MarkAmlResultsAsViewedResponse,
    Api.MarkAmlResultsAsViewedRequest
  >({
    endpoint: 'mark-aml-results-as-viewed',
    investorId,
    suppressNotification: true,
  });

  return {
    kycDetails,
    getKycImages,
    expiredKyc,
    resetKyc,
    approveIdv,
    approveAml,
    manualKycProcess,
    saveAmlApprovalNotes,
    saveIdvApprovalNotes,
    archiveAmlHit,
    unarchiveAmlHit,
    amlResultInformation,
    markAmlResultsAsViewed,
    unapproveAml,
  };
}
